import { useEffect, useState, useCallback } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useReadOnly } from '../hooks/useReadOnly';
import { useAuthStore } from '../store/authStore';
import { authApi, adminApi, portalApi } from '../services/portalApi';
import { usePortalSocket } from '../hooks/usePortalSocket';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  healthDot?: boolean;
}

const ADMIN_NAV: NavItem[] = [
  { to: '/admin/dashboard',     label: 'Live Map',      icon: '🗺️' },
  { to: '/admin/guards',        label: 'Patrollers',    icon: '👮' },
  { to: '/admin/patrols',       label: 'Patrols',       icon: '🔁' },
  { to: '/admin/drones',        label: 'Drones (Alpha)', icon: '🚁' },
  { to: '/admin/events',        label: 'Events',        icon: '📋' },
  { to: '/admin/reports',       label: 'Reports',       icon: '📊' },
  { to: '/admin/portal-users',  label: 'Portal Users',  icon: '👥' },
  { to: '/admin/settings',      label: 'Settings',      icon: '⚙️' },
  { to: '/admin/health',        label: 'System Health', icon: '🩺', healthDot: true },
];

const GUARDHOUSE_NAV: NavItem[] = [
  { to: '/guardhouse/dashboard',  label: 'Live Map',  icon: '🗺️' },
  { to: '/guardhouse/guards',     label: 'Patrollers', icon: '👮' },
  { to: '/guardhouse/patrols',    label: 'Patrols',   icon: '🔁' },
  { to: '/guardhouse/events',     label: 'Events',    icon: '📋' },
  { to: '/guardhouse/reports',    label: 'Reports',   icon: '📊' },
  { to: '/guardhouse/health',    label: 'System Health', icon: '🩺', healthDot: true },
];

const TRUSTEE_NAV: NavItem[] = [
  { to: '/trustee/dashboard',     label: 'Live Map',      icon: '🗺️' },
  { to: '/trustee/guards',        label: 'Patrollers',    icon: '👮' },
  { to: '/trustee/patrols',       label: 'Patrols',       icon: '🔁' },
  { to: '/trustee/events',        label: 'Events',        icon: '📋' },
  { to: '/trustee/portal-users',  label: 'Portal Users',  icon: '👥' },
  { to: '/trustee/settings',      label: 'Settings',      icon: '⚙️' },
  { to: '/trustee/health',        label: 'System Health', icon: '🩺', healthDot: true },
];

const NAV_BY_ROLE = {
  admin:      ADMIN_NAV,
  guardhouse: GUARDHOUSE_NAV,
  trustee:    TRUSTEE_NAV,
};

export default function Layout() {
  const role = useAuthStore((s) => s.role());
  const clearTokens = useAuthStore((s) => s.clearTokens);
  const navigate = useNavigate();
  const readOnly = useReadOnly();
  // Opened once here (not per-page) so the WS connection, and everything
  // accumulated from it — active guards, their breadcrumb tracks, pending
  // alarms — survives navigating between pages. Pages that used to call
  // usePortalSocket() themselves now read this via useOutletContext();
  // calling it per-page tore the connection down and lost the breadcrumb
  // on every navigation away from and back to the live map.
  const portalSocket = usePortalSocket();
  const [estateName, setEstateName] = useState('');
  // null = not yet checked, true = ok, false = degraded/unreachable
  const [healthOk, setHealthOk] = useState<boolean | null>(null);

  useEffect(() => {
    if (role === 'admin') {
      adminApi.getEstate().then((e) => setEstateName(e.name)).catch(() => {});
    } else if (role) {
      portalApi.getEstate().then((e) => setEstateName(e.name)).catch(() => {});
    }
  }, [role]);

  const checkHealth = useCallback(() => {
    portalApi.health()
      .then((h) => setHealthOk(h.db_status === 'ok'))
      .catch(() => setHealthOk(false));
  }, []);

  useEffect(() => {
    checkHealth();
    const id = setInterval(checkHealth, 30_000);
    return () => clearInterval(id);
  }, [checkHealth]);

  const navItems = role ? NAV_BY_ROLE[role] : [];

  async function handleLogout() {
    try { await authApi.logout(); } catch { /* ignore */ }
    clearTokens();
    navigate('/login');
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="w-56 bg-gray-900 flex flex-col flex-shrink-0">
        <div className="px-5 py-5 border-b border-gray-700">
          <span className="text-white font-bold text-lg tracking-tight">PatrolIt</span>
          {estateName && (
            <span className="block text-sm text-gray-300 mt-0.5 truncate" title={estateName}>
              {estateName}
            </span>
          )}
          {role && (
            <span className="block text-xs text-gray-500 capitalize mt-0.5">
              {role === 'trustee' ? 'View only' : role === 'guardhouse' ? 'Control Room' : role}
            </span>
          )}
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`
              }
            >
              <span className="text-base">{item.icon}</span>
              <span className="flex-1">{item.label}</span>
              {item.healthDot && healthOk !== null && (
                <span
                  className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${healthOk ? 'bg-green-400' : 'bg-red-500'}`}
                  title={healthOk ? 'All systems operational' : 'System issue detected'}
                />
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-gray-700">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
          >
            <span>🚪</span> Log out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        {readOnly && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 text-sm text-amber-900">
            <strong>Demo site</strong> — everything you see is live, including the simulated
            patrollers and drones, but changes are disabled.
          </div>
        )}
        <Outlet context={portalSocket} />
      </main>
    </div>
  );
}
